import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatutCommande, statutColors, statutLabels } from '../../../mocks/commandes';
import { formatPrice } from '../../../utils/format';
import { api } from '../../../services/api';
import { checkPermission, PERMISSIONS } from '../../../utils/permissions';

const STATUTS: (StatutCommande | 'TOUS' | 'IMPAYES')[] = ['TOUS', 'IMPAYES', 'EN_ATTENTE', 'EN_COUPE', 'EN_COUTURE', 'TERMINE', 'LIVRE'];

interface CommandesTabProps {
  activeBoutique?: 'toutes' | 1 | 2;
}

export default function CommandesTab({ activeBoutique = 'toutes' }: CommandesTabProps) {
  const navigate = useNavigate();
  const [commandes, setCommandes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtreStatut, setFiltreStatut] = useState<StatutCommande | 'TOUS' | 'IMPAYES'>('TOUS');
  const [search, setSearch] = useState('');
  const [showTrash, setShowTrash] = useState(false);
  const [trashOrders, setTrashOrders] = useState<any[]>([]);

  const userRole = (() => {
    try {
      return JSON.parse(localStorage.getItem('atelier_bassoum_user') || '{}').role;
    } catch {
      return null;
    }
  })();

  const canCreate = checkPermission(PERMISSIONS.CMD_CREER);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) { navigate('/login'); return; }
      try {
        setLoading(true);
        if (showTrash) {
          const data = await api.getTrashCommandes(token);
          setTrashOrders(Array.isArray(data) ? data : []);
        } else {
          const data = await api.getCommandes(token);
          setCommandes(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate, showTrash]);

  const dataSource = showTrash ? trashOrders : commandes;

  const filtered = dataSource.filter(c => {
    // Check status match
    let matchStatut = true;
    if (!showTrash) {
      if (filtreStatut === 'IMPAYES') matchStatut = Number(c.reste) > 0 && c.statut !== 'ANNULEE';
      else if (filtreStatut !== 'TOUS') matchStatut = c.statut === filtreStatut;
    }

    // Check boutique match
    const matchBoutique = activeBoutique === 'toutes' || Number(c.boutique_id) === activeBoutique;

    const searchLow = search.toLowerCase();
    const matchSearch = search === '' || 
      (c.client_nom?.toLowerCase().includes(searchLow)) || 
      (c.code?.toLowerCase().includes(searchLow)) || 
      (c.modele_nom?.toLowerCase().includes(searchLow));
      
    return matchStatut && matchBoutique && matchSearch;
  });

  const handleRestore = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.restoreCommande(token, id);
      const data = await api.getTrashCommandes(token);
      setTrashOrders(data);
    } catch (err) {
      alert("Erreur lors de la restauration");
    }
  };

  const handleHardDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!window.confirm("Supprimer définitivement cette commande ? Cette action est irréversible.")) return;
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    try {
      await api.hardDeleteCommande(token, id);
      const data = await api.getTrashCommandes(token);
      setTrashOrders(data);
    } catch (err) {
      alert("Erreur lors de la suppression définitive");
    }
  };

  return (
    <div className="space-y-4">
      {/* Filters + search */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm" />
          </div>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher une commande, un client..."
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-foret-400 bg-white"
          />
        </div>
        <div className="flex gap-2">
           <button
             onClick={() => setShowTrash(!showTrash)}
             className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer transition-colors border ${
               showTrash ? 'bg-red-50 text-red-600 border-red-200' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
             }`}
             title={showTrash ? "Retour aux commandes" : "Voir la corbeille"}
           >
             <i className={showTrash ? "ri-arrow-left-line" : "ri-delete-bin-line"} />
             {showTrash ? "Retour" : "Corbeille"}
           </button>
           {canCreate && (
             <button
               onClick={() => navigate('/commande/nouvelle')}
               className="flex items-center gap-2 bg-foret-500 text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer hover:bg-foret-600 whitespace-nowrap transition-colors"
             >
               <div className="w-4 h-4 flex items-center justify-center">
                 <i className="ri-add-line" />
               </div>
               Nouvelle commande
             </button>
           )}
        </div>
      </div>

      {/* Status tabs (hide in trash) */}
      {!showTrash && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {STATUTS.map(s => {
            let count = 0;
            if (s === 'TOUS') count = commandes.length;
            else if (s === 'IMPAYES') count = commandes.filter(c => Number(c.reste) > 0 && c.statut !== 'ANNULEE').length;
            else count = commandes.filter(c => c.statut === s).length;
            
            return (
              <button
                key={s}
                onClick={() => setFiltreStatut(s)}
                className={`flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  filtreStatut === s 
                    ? (s === 'IMPAYES' ? 'bg-red-500 text-white shadow-sm' : 'bg-foret-500 text-white shadow-sm')
                    : (s === 'IMPAYES' ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')
                }`}
              >
                {s === 'TOUS' ? 'Toutes' : (s === 'IMPAYES' ? 'Impayés' : statutLabels[s as StatutCommande])}
                <span className={`text-xs ${filtreStatut === s ? 'bg-white/20 text-white' : 'bg-white text-gray-500 shadow-sm'} rounded-full px-1.5 py-0.5`}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {showTrash && (
        <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-2 flex items-center gap-2">
            <i className="ri-information-line text-red-500" />
            <p className="text-xs text-red-700 font-semibold">Corbeille : Liste des commandes annulées ou supprimées.</p>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {['Référence', 'Client', 'Modèle', 'Date commande', 'Livraison', 'Total', 'Reste', 'Statut', ''].map(h => (
                  <th key={h} className="text-left text-xs font-semibold text-gray-400 uppercase tracking-wider px-4 py-3 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-gray-400 text-sm">Chargement...</td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center py-10 text-gray-400 text-sm">Aucune commande trouvée</td>
                </tr>
              )}
              {!loading && filtered.map(c => (
                <tr key={c.id} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/commande/${c.id}`)}>
                  <td className="px-4 py-3.5 text-xs font-mono text-foret-600 font-bold">{c.code}</td>
                  <td className="px-4 py-3.5">
                    <p className="text-sm font-medium text-gray-800">{c.client_nom}</p>
                    <p className="text-xs text-gray-400">Client ID: {c.client_id}</p>
                  </td>
                  <td className="px-4 py-3.5 text-xs text-gray-600 max-w-36 truncate">{c.modele_nom || 'Sur mesure'}</td>
                  <td className="px-4 py-3.5 text-xs text-gray-400">{new Date(c.date_commande).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</td>
                  <td className="px-4 py-3.5 text-xs text-gray-500">{c.date_livraison ? new Date(c.date_livraison).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : '-'}</td>
                  <td className="px-4 py-3.5 text-sm font-semibold text-gray-800">{formatPrice(c.total, false)}</td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs font-semibold ${c.reste > 0 ? 'text-red-600' : 'text-foret-600'}`}>
                      {c.reste > 0 ? formatPrice(c.reste, false) : 'Soldé'}
                    </span>
                  </td>
                  <td className="px-4 py-3.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium whitespace-nowrap ${statutColors[c.statut as StatutCommande] || 'bg-gray-100'}`}>{statutLabels[c.statut as StatutCommande] || c.statut}</span>
                  </td>
                  <td className="px-4 py-3.5">
                    <div className="flex items-center gap-2 justify-end">
                      {showTrash ? (
                        <>
                          <button 
                            onClick={(e) => handleRestore(e, c.id)}
                            className="w-8 h-8 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 flex items-center justify-center transition-colors"
                            title="Restaurer"
                          >
                            <i className="ri-refresh-line" />
                          </button>
                          <button 
                            onClick={(e) => handleHardDelete(e, c.id)}
                            className="w-8 h-8 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 flex items-center justify-center transition-colors"
                            title="Supprimer définitivement"
                          >
                            <i className="ri-delete-bin-line" />
                          </button>
                        </>
                      ) : (
                        <div className="flex items-center gap-2">
                          {(userRole === 'Admin' || userRole === 'Gérant') && c.statut !== 'ANNULEE' && (
                            <button 
                              onClick={async (e) => {
                                e.stopPropagation();
                                if(!window.confirm("Êtes-vous sûr de vouloir annuler cette commande ? Les tissus seront remis en stock.")) return;
                                const token = localStorage.getItem('atelier_bassoum_token');
                                if(!token) return;
                                try {
                                  await api.cancelCommande(token, c.id);
                                  setCommandes(prev => prev.filter(cmd => cmd.id !== c.id));
                                } catch (err) {
                                  alert("Erreur lors de l'annulation.");
                                }
                              }}
                              className="w-7 h-7 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"
                              title="Annuler la commande"
                            >
                              <i className="ri-delete-bin-line" />
                            </button>
                          )}
                          <div className="w-5 h-5 flex items-center justify-center text-gray-300 hover:text-foret-500 transition-colors">
                            <i className="ri-arrow-right-s-line" />
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
